import { toast } from "react-toastify";

import {
    ToastPositions,
    DEFAULT_POSITION,
    DEFAULT_DELAY,
    DEFAULT_HIDE_PROGRESS_FLAG,
    DEFAULT_CLOSE_ON_CLICK_FLAG,
    DEFAULT_PAUSE_ON_HOVER_FLAG,
    DEFAULT_DRAGGABLE_FLAG
} from "./toaster.default_values";

export interface ToastOptions {
    position?: ToastPositions;
    autoClose?: number;
    hideProgressBar?: boolean;
    closeOnClick?: boolean;
    pauseOnHover?: boolean;
    draggable?: boolean;
}

/**
 * Default options for any presentable toast
 */
export const defaultOptions: ToastOptions = {
    position: DEFAULT_POSITION,
    autoClose: DEFAULT_DELAY,
    hideProgressBar: DEFAULT_HIDE_PROGRESS_FLAG,
    closeOnClick: DEFAULT_CLOSE_ON_CLICK_FLAG,
    pauseOnHover: DEFAULT_PAUSE_ON_HOVER_FLAG,
    draggable: DEFAULT_DRAGGABLE_FLAG
};

export class ToasterService {
    /**
     * Creates a toast of the 'info' type with provided messages and options.
     *
     * @param message {string} - Message to be presented in Toast.
     * @param opts {ToastOptions} - Additional options to override defaults.
     */
    static infoToast = (
        message: string,
        opts: ToastOptions = defaultOptions
    ) => {
        _makeToast(toast.info, message, opts);
    };

    /**
     * Creates a toast of the 'success' type with provided messages and options.
     *
     * @param message {string} - Message to be presented in Toast.
     * @param opts {ToastOptions} - Additional options to override defaults.
     */
    static successToast = (
        message: string,
        opts: ToastOptions = defaultOptions
    ) => {
        _makeToast(toast.success, message, opts);
    };

    /**
     * Creates a toast of the 'warning' type with provided messages and options.
     *
     * @param message {string} - Message to be presented in Toast.
     * @param opts {ToastOptions} - Additional options to override defaults.
     */
    static warningToast = (
        message: string,
        opts: ToastOptions = defaultOptions
    ) => {
        _makeToast(toast.warn, message, opts);
    };

    /**
     * Creates a toast of the 'error' type with provided messages and options.
     *
     * @param message {string} - Message to be presented in Toast.
     * @param opts {ToastOptions} - Additional options to override defaults.
     */
    static errorToast = (
        message: string,
        opts: ToastOptions = defaultOptions
    ) => {
        _makeToast(toast.error, message, opts);
    };

    /**
     * Creates a toast of the 'default' type with provided messages and options.
     *
     * @param message {string} - Message to be presented in Toast.
     * @param opts {ToastOptions} - Additional options to override defaults.
     */
    static defaultToast = (
        message: string,
        opts: ToastOptions = defaultOptions
    ) => {
        _makeToast(toast, message, opts);
    };
}

/**
 * Calls the toaster function passed as argument with the gibven message,
 * and additional options.
 *
 * Options can be partial, meaning that properties of ToastOptions that
 * are not provided will be assumed to be the defaults.
 * @param toastCall {(message, opts) => void} - Call function to Toast.
 * @param message {string} - Message to be presented in Toast.
 * @param options {ToastOptions} - Additional options to override defaults.
 */
export function _makeToast(
    toastCall: (message, opts) => void,
    message: string,
    options: ToastOptions = defaultOptions
) {
    let callOpts = { ...defaultOptions };
    if (options) {
        for (const opt in options) {
            callOpts[opt] = options[opt];
        }
    }
    toastCall(message, callOpts);
}
